/**
 * Authentication Context for Admin Panel
 * Manages user authentication state and provides login/logout functionality
 */

import { createContext, useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { adminApi } from "../services/api";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, []);

  // Load current user
  const loadUser = async () => {
    try {
      const response = await adminApi.getMe();
      setUser(response);
      setError(null);
    } catch (err) {
      console.error("Load user error:", err);
      localStorage.removeItem("adminToken");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Login
  const login = async (credentials) => {
    try {
      setError(null);
      const response = await adminApi.login(credentials);
      const { token, user: userData } = response;

      localStorage.setItem("adminToken", token);
      setUser(userData);

      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || "Login failed. Please check your credentials.";
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem("adminToken");
    setUser(null);
    setError(null);
  };

  // Helper: check if user has permission for a page
  const hasPermission = (page) => {
    if (!user) return false;

    // Global admin always gets full access
    if (user.isGlobalAdmin) return true;

    // These are always granted to every logged-in user. "timesheet" is here
    // deliberately — every staff member logs their own hours, so it's not a
    // per-page permission like the admin content/tools pages below it.
    if (["dashboard", "profile", "logout", "timesheet"].includes(page)) return true;

    // manage-users is strictly global-admin only — never granted via the permissions object
    if (page === "manage-users") return false;

    // "meetings" and "bulk-message" don't have their own toggle in Manage Users —
    // they piggyback on an existing one (consultations, autoresponder) since the
    // two pages are closely related to those. Keep this in sync with routes.js:
    // whatever key a route's <PrivateRoute permission="..."> actually checks is
    // what belongs on the left below, or the sidebar and the route disagree on
    // whether the page is visible/reachable.
    const ALIASED_PAGES = { meetings: "consultations", "bulk-message": "autoresponder" };
    const effectivePage = ALIASED_PAGES[page] || page;

    // If this user has no explicit permissions set yet (legacy / freshly created admin),
    // fall back to their role so they don't get locked out
    const perms = user.permissions;
    if (!perms || Object.keys(perms).length === 0) {
      return user.role === "ADMIN";
    }

    // Otherwise honour only what has been explicitly granted
    return perms[effectivePage] === true;
  };

  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
